import { useMediaQuery, useTheme } from '@mui/material';
import type { ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiSend as SendIcon } from 'react-icons/fi';
import { useSelector } from 'react-redux';

import AgGrid from 'src/components/AgGrid';
import { TrashIcon } from 'src/components/icons';
import { CONTRACT_DETAILS_PAGE, CONTRACTS_PAGE } from 'src/constants';
import { useFeatureFlags } from 'src/context/flagsContext';
import history from 'src/history';
import { useModuleIcon } from 'src/hooks/useModuleIcon';
import { TextCellRenderer } from 'src/legacy/components/AgGrid/Renderer/TextCellRenderer';
import { BaseActionsMenu } from 'src/legacy/components/Dropdowns';
import { LoadingWrapper } from 'src/legacy/components/Loading';
import { NonDesktopActionTooltip } from 'src/legacy/components/Tooltip';
import { CreateButton } from 'src/legacy/components/UI';
import StandardPage from 'src/legacy/components/UI/StandardPage';
import {
  ContractTemplate,
  useArchiveContractTemplateMutation,
  useGetContractTemplateCountsQuery,
  useGetContractTemplatesLiteQuery,
  useGetContractTemplatesQuery,
} from 'src/services/api';
import { RootState } from 'src/store';
import DateUtils from 'src/utils/DateUtils';

import { TABS_CONTAINER_HEIGHT } from '../UI/BaseTab';
import { TableWrapper } from '../UI/Tables/TableWrapper';

import { sortContractTemplatesByCreatedDate } from './contract_helpers';
import { ContractDetailsPageView } from './ContractDetailsPage';
import { ShareContractModal } from './ShareContractModal';

export const templateListcolumns: ColDef[] = [
  {
    headerName: 'Name',
    field: 'name',
    minWidth: 300,
    sortable: true,
  },
  {
    headerName: 'Waiting for submission',
    field: 'requestsCount',
    minWidth: 200,
    sortable: true,
    cellClass: 'rightAlignedColumn',
    headerClass: 'rightAlignedHeader',
    cellRenderer: 'textCellRenderer',
  },
  {
    headerName: 'Submissions',
    field: 'submissionsCount',
    minWidth: 200,
    sortable: true,
    cellClass: 'rightAlignedColumn',
    headerClass: 'rightAlignedHeader',
    cellRenderer: 'textCellRenderer',
  },
  {
    headerName: 'Latest submission',
    field: 'latestSubmissionDate',
    minWidth: 200,
    sortable: true,
    cellClass: 'rightAlignedColumn',
    headerClass: 'rightAlignedHeader',
    cellRenderer: 'textCellRenderer',
    getQuickFilterText(filterParams) {
      return DateUtils.formatDate(filterParams.data.sentOn);
    },
    valueFormatter: ({ value }) => (value ? DateUtils.formatDate(value) : ''),
  },
];

interface TemplateActionMenuProps {
  data: ContractTemplate;
  handleShareModalOpen: (data: ContractTemplate) => void;
}

/**
 * Component to render the table row action for contract templates
 */
const TemplateActionMenu = ({
  data,
  handleShareModalOpen,
}: TemplateActionMenuProps) => {
  const [archiveTemplate] = useArchiveContractTemplateMutation();

  return (
    <BaseActionsMenu
      actions={[
        {
          name: 'Send',
          icon: <SendIcon />,
          onClick: () => handleShareModalOpen(data),
        },
        {
          name: 'Archive',
          icon: <TrashIcon />,
          onClick: () => archiveTemplate({ contractTemplateId: data.id }),
          isDelete: true,
        },
      ]}
    />
  );
};

type TemplateListTableProps = {
  onClickNewContract?: () => void;
};

export const TemplateListTable = ({
  onClickNewContract,
}: TemplateListTableProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const [shareModalInfo, setShareModalInfo] = useState<{
    isOpen: boolean;
    selectedTemplate: ContractTemplate | undefined;
  }>({
    isOpen: false,
    selectedTemplate: undefined,
  });

  const isDesktopScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { EnableContractTemplatesPerf } = useFeatureFlags();

  const liteResult = useGetContractTemplatesLiteQuery(undefined, {
    skip: !EnableContractTemplatesPerf,
  });
  const legacyResult = useGetContractTemplatesQuery(undefined, {
    skip: EnableContractTemplatesPerf,
  });
  const { data, isLoading: areTemplatesLoading } = EnableContractTemplatesPerf
    ? liteResult
    : legacyResult;

  const { data: countsData, isLoading: areCountsLoading } =
    useGetContractTemplateCountsQuery(undefined, {
      skip: !EnableContractTemplatesPerf,
    });

  const Icon = useModuleIcon('contracts');

  const templates = useMemo(() => {
    const sortedTemplates = sortContractTemplatesByCreatedDate(data);
    if (!EnableContractTemplatesPerf) return sortedTemplates;
    if (!countsData) return sortedTemplates;
    const countsByTemplateId = new Map(
      countsData.map((c) => [c.templateId, c]),
    );
    return sortedTemplates.map((template) => {
      const counts = countsByTemplateId.get(template.id);
      if (!counts) return template;
      return {
        ...template,
        requestsCount: counts.requestsCount,
        submissionsCount: counts.submissionsCount,
        latestSubmissionDate: counts.latestSubmissionDate,
      };
    });
  }, [EnableContractTemplatesPerf, data, countsData]);

  const columnDefs = useMemo<ColDef[]>(() => {
    const showCountsPlaceholder =
      EnableContractTemplatesPerf && areCountsLoading;
    return templateListcolumns.map((col) => {
      if (col.field === 'requestsCount' || col.field === 'submissionsCount') {
        return {
          ...col,
          valueFormatter: ({ value }) => {
            if (showCountsPlaceholder) return '—';
            if (value === undefined || value === null) return '—';
            return String(value);
          },
        };
      }
      if (col.field === 'latestSubmissionDate') {
        return {
          ...col,
          valueFormatter: ({ value }) => {
            if (showCountsPlaceholder) return '—';
            return value ? DateUtils.formatDate(value) : '';
          },
        };
      }
      return col;
    });
  }, [EnableContractTemplatesPerf, areCountsLoading, templateListcolumns]);

  /**
   * Opens the contract share modal.
   * If the selected template has variable inputs, the modal will render those inputs.
   * @param rowData - Selected template information
   */
  const handleShareModalOpen = (rowData: ContractTemplate) => {
    if (!rowData) return;

    setShareModalInfo({
      isOpen: true,
      selectedTemplate: rowData,
    });
  };

  const handleShareModalClose = () => {
    setShareModalInfo({
      isOpen: false,
      selectedTemplate: undefined,
    });
  };

  const handleRowClick = (rowData: ContractTemplate) => {
    const { id } = rowData;
    history.push(
      `${CONTRACT_DETAILS_PAGE.path}?templateId=${id}&view=${ContractDetailsPageView.SubmissionRequests}`,
    );
  };

  const handleClickNewContract = () => {
    if (onClickNewContract) {
      onClickNewContract();
      return;
    }
    history.push(`${CONTRACTS_PAGE.path}/edit`);
  };

  const searchKey = useSelector((state: RootState) => state.ui.searchValue);

  const shouldShowStandardPage = !templates || templates.length === 0;

  return (
    <LoadingWrapper isLoading={areTemplatesLoading} hideContentWhileLoading>
      {shouldShowStandardPage ? (
        <StandardPage
          topIcon={Icon}
          noClientTitle={t(($) => $.messages.no_client.title, {
            actionItem: 'sharing contracts',
          })}
          noClientDesc={t(($) => $.messages.no_client.description, {
            actionItem: `share contracts with ${t(($) => $.client, { count: 2 }).toLowerCase()}`,
          })}
          title="No contracts have been created yet"
          desc={t(($) => $.page_contracts.contracts_description)}
          actionButton={
            <NonDesktopActionTooltip>
              <CreateButton
                htmlId="btn-create-contracts-template"
                onClick={handleClickNewContract}
                disabled={!isDesktopScreen}
              >
                Create contract
              </CreateButton>
            </NonDesktopActionTooltip>
          }
        />
      ) : (
        <TableWrapper
          className="px-5"
          height={`calc(100% - ${TABS_CONTAINER_HEIGHT + 12}px)`}
        >
          <AgGrid
            className="agGridRowHover"
            rowData={templates}
            columnDefs={[
              ...columnDefs,
              {
                colId: 'actions',
                headerName: 'Actions',
                suppressSizeToFit: true,
                pinned: isSm ? 'right' : null,
                cellRenderer: 'templateActionRenderer',
                cellRendererParams: {
                  handleShareModalOpen,
                },
              },
            ]}
            searchKey={searchKey}
            frameworkComponents={{
              templateActionRenderer: TemplateActionMenu,
              textCellRenderer: TextCellRenderer,
            }}
            onRowClicked={handleRowClick}
          />
          <ShareContractModal
            open={shareModalInfo.isOpen}
            selectedTemplate={shareModalInfo.selectedTemplate}
            onClose={handleShareModalClose}
          />
        </TableWrapper>
      )}
    </LoadingWrapper>
  );
};
